import { TeammateAgentBranch } from "@repo/app-types/entities/teammateAgent";
import { useCallback, useState } from "react";
import { useLazyGetTeammateAgentQuery } from "../api/entities/teammateAgent.js";

const DEFAULT_MAX_ATTEMPTS = 30;
const DEFAULT_POLL_INTERVAL_MS = 2000;

interface UseWaitForAgentPromptOptions {
  teammateAgentId: string;
  /** Maximum number of polling attempts (default: 30) */
  maxAttempts?: number;
  /** Interval between polls in milliseconds (default: 2000) */
  pollIntervalMs?: number;
}

interface UseWaitForAgentPromptResult {
  /** Whether we're currently waiting for the prompt to be ready */
  isWaiting: boolean;
  /**
   * Wait for the agent's draft prompt to be ready (not stale).
   * Polls until prompt_is_stale is false or max attempts reached.
   * @returns true if prompt is ready, false if timed out
   */
  waitForPromptReady: () => Promise<boolean>;
}

/**
 * Hook that provides a function to wait for a teammate agent's draft prompt
 * to be ready (regenerated after config changes).
 *
 * This is needed because when agent config or playbook changes are saved,
 * the prompt is marked as stale and regenerated asynchronously in the background.
 * Features like "Try this agent" and "Run tests" need to wait for the prompt
 * to be fresh before proceeding.
 */
export function useWaitForAgentPrompt({
  teammateAgentId,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}: UseWaitForAgentPromptOptions): UseWaitForAgentPromptResult {
  const [isWaiting, setIsWaiting] = useState(false);
  const [fetchDraftAgent] = useLazyGetTeammateAgentQuery();

  const waitForPromptReady = useCallback(async (): Promise<boolean> => {
    setIsWaiting(true);

    try {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const { data: draftAgent } = await fetchDraftAgent({
          id: teammateAgentId,
          branch: TeammateAgentBranch.Draft,
        });

        // If prompt is not stale (or undefined for backwards compatibility), we're ready
        if (!draftAgent?.prompt_is_stale) {
          return true;
        }

        // Wait before polling again (skip wait on last attempt)
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
        }
      }

      // Timed out waiting for prompt
      return false;
    } finally {
      setIsWaiting(false);
    }
  }, [teammateAgentId, maxAttempts, pollIntervalMs, fetchDraftAgent]);

  return {
    isWaiting,
    waitForPromptReady,
  };
}
